import React, { useState } from 'react';

/**
 * AppointmentForm - Modern appointment booking form
 * @param {Object} props
 * @param {function} props.onSubmit - Form submit handler
 * @param {function} props.onClose - Close handler
 */
export const AppointmentForm = ({ onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    mobile: '',
    dateToCall: '',
    timePreferred: '',
    languagePreferred: 'English',
    consultationOn: 'University Selection',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit(formData);
    }
    console.log('Appointment form submitted:', formData);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm" data-testid="appointment-form-modal">
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Book an Appointment</h2>
              <p className="text-sm text-gray-500 mt-0.5">Fill in your details and we'll reach out to you</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-white hover:shadow-sm transition-all duration-150"
              aria-label="Close"
              data-testid="appointment-form-close"
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Name */}
            <div className="md:col-span-2">
              <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent focus:bg-white transition-all duration-150"
                placeholder="Your full name"
                data-testid="appointment-form-name"
              />
            </div>

            {/* Country */}
            <div>
              <label htmlFor="country" className="block text-sm font-semibold text-gray-700 mb-2">
                Country *
              </label>
              <input
                type="text"
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                required
                className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent focus:bg-white transition-all duration-150"
                placeholder="Your country"
                data-testid="appointment-form-country"
              />
            </div>

            {/* Mobile Number */}
            <div>
              <label htmlFor="mobile" className="block text-sm font-semibold text-gray-700 mb-2">
                Mobile Number *
              </label>
              <input
                type="tel"
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                required
                className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent focus:bg-white transition-all duration-150"
                placeholder="+60 123 456 789"
                data-testid="appointment-form-mobile"
              />
            </div>

            {/* Date to Call */}
            <div>
              <label htmlFor="dateToCall" className="block text-sm font-semibold text-gray-700 mb-2">
                Date to Call *
              </label>
              <input
                type="date"
                id="dateToCall"
                name="dateToCall"
                value={formData.dateToCall}
                onChange={handleChange}
                required
                className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent focus:bg-white transition-all duration-150"
                data-testid="appointment-form-date"
              />
            </div>

            {/* Time Preferred */}
            <div>
              <label htmlFor="timePreferred" className="block text-sm font-semibold text-gray-700 mb-2">
                Time Preferred *
              </label>
              <select
                id="timePreferred"
                name="timePreferred"
                value={formData.timePreferred}
                onChange={handleChange}
                required
                className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent focus:bg-white transition-all duration-150 appearance-none cursor-pointer"
                data-testid="appointment-form-time"
              >
                <option value="">Select time</option>
                <option value="09:00-12:00">Morning (9:00 AM - 12:00 PM)</option>
                <option value="12:00-15:00">Afternoon (12:00 PM - 3:00 PM)</option>
                <option value="15:00-18:00">Evening (3:00 PM - 6:00 PM)</option>
              </select>
            </div>

            {/* Language Preferred */}
            <div>
              <label htmlFor="languagePreferred" className="block text-sm font-semibold text-gray-700 mb-2">
                Language Preferred *
              </label>
              <select
                id="languagePreferred"
                name="languagePreferred"
                value={formData.languagePreferred}
                onChange={handleChange}
                required
                className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent focus:bg-white transition-all duration-150 appearance-none cursor-pointer"
                data-testid="appointment-form-language"
              >
                <option value="English">English</option>
                <option value="Bahasa Malaysia">Bahasa Malaysia</option>
                <option value="Chinese">中文 (Chinese)</option>
              </select>
            </div>

            {/* Consultation On */}
            <div>
              <label htmlFor="consultationOn" className="block text-sm font-semibold text-gray-700 mb-2">
                Consultation On *
              </label>
              <select
                id="consultationOn"
                name="consultationOn"
                value={formData.consultationOn}
                onChange={handleChange}
                required
                className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent focus:bg-white transition-all duration-150 appearance-none cursor-pointer"
                data-testid="appointment-form-consultation"
              >
                <option value="Scholarship">Scholarship</option>
                <option value="University Selection">University Selection</option>
                <option value="Admission Support">Admission Support</option>
                <option value="Campus Tour">Campus Tour</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-8 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-12 px-6 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all duration-150 font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 h-12 px-6 rounded-xl bg-gradient-to-r from-orange-500 to-orange-400 text-white hover:from-orange-600 hover:to-orange-500 transition-all duration-150 font-semibold shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-300"
              data-testid="appointment-form-submit"
            >
              Book Appointment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
